/**
 * HalftimeOverlay - Shows halftime stats and break countdown.
 * Covers tasks 1.14 (halftime break), 3.4 (match statistics overlay at half time).
 */

import * as GUI from "babylonjs-gui";
import { GameScore } from "../systems/GameState";
import { MatchStats } from "../systems/MatchStatsSystem";

export class HalftimeOverlay {
  private _texture: GUI.AdvancedDynamicTexture;
  private _container: GUI.Rectangle;
  private _countdownText: GUI.TextBlock;
  private _statsPanel: GUI.StackPanel;
  private _isVisible = false;

  constructor() {
    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("halftimeOverlay");

    this._container = new GUI.Rectangle("halftimeBg");
    this._container.width = "100%";
    this._container.height = "100%";
    this._container.thickness = 0;
    this._container.background = "rgba(0,0,0,0.75)";
    this._container.isVisible = false;
    this._texture.addControl(this._container);

    // Halftime title
    const title = new GUI.TextBlock("halftimeTitle", "⏱ HALF TIME");
    title.color = "#ffcc00";
    title.fontSize = 54;
    title.fontWeight = "900";
    title.fontFamily = "Orbitron, sans-serif";
    title.outlineWidth = 3;
    title.outlineColor = "black";
    title.top = "-220px";
    this._container.addControl(title);

    // Score display
    const scoreLabel = new GUI.TextBlock("htScoreLabel", "FIRST HALF SCORE");
    scoreLabel.color = "#aaaaaa";
    scoreLabel.fontSize = 12;
    scoreLabel.fontWeight = "700";
    scoreLabel.fontFamily = "Orbitron, sans-serif";
    scoreLabel.top = "-155px";
    this._container.addControl(scoreLabel);

    // Stats panel
    this._statsPanel = new GUI.StackPanel("htStatsPanel");
    this._statsPanel.width = "360px";
    this._statsPanel.height = "180px";
    this._statsPanel.top = "-30px";
    this._statsPanel.isVertical = true;
    this._container.addControl(this._statsPanel);

    // Countdown text
    this._countdownText = new GUI.TextBlock("countdown", "RESUMING IN 5...");
    this._countdownText.color = "#00ff88";
    this._countdownText.fontSize = 18;
    this._countdownText.fontWeight = "700";
    this._countdownText.fontFamily = "Orbitron, sans-serif";
    this._countdownText.top = "120px";
    this._container.addControl(this._countdownText);

    // Second half label
    const secondHalf = new GUI.TextBlock("secondHalf", "GET READY FOR THE SECOND HALF!");
    secondHalf.color = "white";
    secondHalf.fontSize = 14;
    secondHalf.fontFamily = "Orbitron, sans-serif";
    secondHalf.top = "155px";
    this._container.addControl(secondHalf);
  }

  show(firstHalfScore: GameScore, stats?: MatchStats): void {
    this._container.isVisible = true;
    this._isVisible = true;

    // Clear and rebuild stats
    this._statsPanel.clearControls();

    // First half score
    this._addScoreRow(firstHalfScore);

    if (stats) {
      const poss = Math.round(
        (stats.playerPossessionTime /
          Math.max(0.1, stats.playerPossessionTime + stats.opponentPossessionTime)) * 100
      );
      this._addStatRow("POSSESSION", `${poss}%`, `${100 - poss}%`);
      this._addStatRow("SHOTS ON TARGET", `${stats.playerShotsOnTarget}`, `${stats.opponentShotsOnTarget}`);
      this._addStatRow("TOTAL SHOTS", `${stats.playerShots}`, `${stats.opponentShots}`);
    }
  }

  private _addScoreRow(score: GameScore): void {
    const row = new GUI.StackPanel("scoreRow");
    row.isVertical = false;
    row.height = "50px";
    row.width = "360px";
    this._statsPanel.addControl(row);

    const pScore = new GUI.TextBlock("pScore", `${score.player}`);
    pScore.color = "#88aaff";
    pScore.fontSize = 36;
    pScore.fontWeight = "900";
    pScore.fontFamily = "Orbitron, sans-serif";
    pScore.width = "80px";
    pScore.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    row.addControl(pScore);

    const dash = new GUI.TextBlock("dash", " - ");
    dash.color = "white";
    dash.fontSize = 36;
    dash.fontWeight = "900";
    dash.fontFamily = "Orbitron, sans-serif";
    dash.width = "60px";
    dash.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    row.addControl(dash);

    const oScore = new GUI.TextBlock("oScore", `${score.opponent}`);
    oScore.color = "#ff8888";
    oScore.fontSize = 36;
    oScore.fontWeight = "900";
    oScore.fontFamily = "Orbitron, sans-serif";
    oScore.width = "80px";
    oScore.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    row.addControl(oScore);
  }

  private _addStatRow(label: string, playerVal: string, opponentVal: string): void {
    const row = new GUI.StackPanel(`ht_${label}`);
    row.isVertical = false;
    row.height = "26px";
    row.width = "360px";
    this._statsPanel.addControl(row);

    const pText = new GUI.TextBlock(`htp_${label}`, playerVal);
    pText.color = "#88aaff";
    pText.fontSize = 12;
    pText.fontWeight = "700";
    pText.fontFamily = "Orbitron, sans-serif";
    pText.width = "80px";
    pText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    row.addControl(pText);

    const lText = new GUI.TextBlock(`htl_${label}`, label);
    lText.color = "#888888";
    lText.fontSize = 10;
    lText.fontFamily = "Orbitron, sans-serif";
    lText.width = "180px";
    lText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    row.addControl(lText);

    const oText = new GUI.TextBlock(`hto_${label}`, opponentVal);
    oText.color = "#ff8888";
    oText.fontSize = 12;
    oText.fontWeight = "700";
    oText.fontFamily = "Orbitron, sans-serif";
    oText.width = "80px";
    oText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    row.addControl(oText);
  }

  updateCountdown(secondsLeft: number): void {
    this._countdownText.text = `RESUMING IN ${Math.ceil(secondsLeft)}...`;
  }

  hide(): void {
    this._container.isVisible = false;
    this._isVisible = false;
  }

  get visible(): boolean {
    return this._isVisible;
  }

  dispose(): void {
    this._texture.dispose();
  }
}
